import { memo, useRef, useState } from 'react';
import type { VideoPreviewForInsertProps } from './types';
import Icon from '@/components/atoms/Icon';
import Loader from '@/components/atoms/Loader';

function VideoPreviewForInsert({ url, onSelect, onDelete, onClose }: VideoPreviewForInsertProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  function playVideoOnHover() {
    if (videoRef.current && videoRef.current.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      videoRef.current.play();
    }
  }

  function pauseVideo() {
    if (videoRef.current && videoRef.current.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      videoRef.current.pause();
    }
  }

  function handleSelect() {
    if (isLoaded && videoRef.current) {
      onSelect(url, videoRef.current.duration);
      onClose();
    }
  }

  function onVideoLoaded() {
    setIsLoaded(true);
  }

  const getVideoMimeType = (src: string) => (src.toLowerCase().endsWith('.mov') ? 'video/quicktime' : 'video/mp4');

  const handleDelete = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (onDelete) {
      onDelete();
    }
  };

  return (
    <div
      role="button"
      className="group relative aspect-video w-full cursor-pointer overflow-hidden rounded-lg border"
      onMouseEnter={playVideoOnHover}
      onMouseLeave={pauseVideo}
      onClick={handleSelect}
    >
      {onDelete && (
        <div
          className="absolute right-1 top-1 z-10 opacity-0 transition-opacity duration-200 group-hover:opacity-100"
          onClick={handleDelete}
        >
          <div className="rounded-full bg-black/50 p-1">
            <Icon name="IconTrash" width={16} height={16} color="white" />
          </div>
        </div>
      )}
      <video className="absolute" ref={videoRef} muted onCanPlay={onVideoLoaded}>
        <source src={url} type={getVideoMimeType(url)} />
      </video>
      {!isLoaded && (
        <div className="absolute inset-1/2 h-8 w-[40px] -translate-x-1/2 -translate-y-1/2">
          <Loader />
        </div>
      )}
    </div>
  );
}

export default memo(VideoPreviewForInsert);
